<!--
  HeroCarouselEditor.vue

  Chain: Store editor closing chain, PR E2 (Hero carousel UI).

  Inline editor for a single hero-carousel slide. Opens beneath the
  thumbnail strip in StoreBranding.vue when a thumbnail is clicked.

  Props:
    slide -- the CarouselSlide row being edited
             ({ id, headline, click_through_url, image_file_id, ... }).

  Emits:
    save({ id, headline, click_through_url })  -- parent PATCHes the row
    delete()                                    -- parent DELETEs the row

  The parent owns all backend plumbing; this component is pure UI and
  emits intent. Click-through URL is OPTIONAL but must validate as a
  URL when set. Delete uses a two-step confirm to avoid accidental
  destruction (first click reveals "Confirm delete"; second click
  emits delete).

  AUTO-SAVE (fix/hero-editor-autosave): headline + URL persist on their
  own to match every other field on the store editor -- there is no Save
  button. Edits debounce ~600ms after the last keystroke and flush
  immediately on blur. A pending edit is also flushed when the parent
  swaps in a different slide (user clicked another thumbnail) or when the
  component unmounts, so a fast edit-then-switch never drops the change.
  An invalid URL is never persisted (the error shows instead); once
  corrected, auto-save resumes. Opening a slide seeds the drafts WITHOUT
  emitting -- only real user edits persist (no phantom PATCH on open).

  The save payload carries the slide `id` so the parent PATCHes the
  correct row even when the flush fires after selection has already moved
  to a different slide.
-->
<template>
    <div
        class="carousel-editor"
        data-testid="ct-branding-carousel-editor"
    >
        <!--
            Headline + click-through URL sit SIDE BY SIDE (two-across) to
            save vertical space -- this editor is used on smaller screens
            where the stacked layout pushed the delete action below the
            fold. The row collapses back to stacked under 720px (the same
            responsive boundary the Brand identity card uses, ADR 0024).

            Each field carries helper copy wired to its input via
            aria-describedby, so screen readers announce the explanation
            with the field rather than leaving it as orphaned text. The ids
            are namespaced by slide id -- several editors can be mounted
            across a session and duplicate ids would mis-associate.
        -->
        <div
            class="carousel-editor__fields"
            data-testid="ct-carousel-editor-fields-row"
        >
            <div class="carousel-editor__row">
                <label class="carousel-editor__label" :for="`carousel-editor-headline-${slide.id}`">
                    Headline (optional)
                </label>
                <p
                    :id="`carousel-editor-headline-hint-${slide.id}`"
                    class="carousel-editor__hint"
                    data-testid="ct-carousel-editor-headline-hint"
                >
                    Text shown over this slide on your storefront. Leave it
                    blank to show the image on its own.
                </p>
                <input
                    :id="`carousel-editor-headline-${slide.id}`"
                    v-model="headlineDraft"
                    type="text"
                    class="carousel-editor__input"
                    placeholder="Optional overlay headline"
                    :aria-describedby="`carousel-editor-headline-hint-${slide.id}`"
                    data-testid="ct-carousel-editor-headline"
                    @input="onFieldInput"
                    @blur="onFieldBlur"
                />
            </div>

            <div class="carousel-editor__row">
                <label class="carousel-editor__label" :for="`carousel-editor-url-${slide.id}`">
                    Click-through URL (optional)
                </label>
                <p
                    :id="`carousel-editor-url-hint-${slide.id}`"
                    class="carousel-editor__hint"
                    data-testid="ct-carousel-editor-url-hint"
                >
                    Where a shopper lands when they click this slide. Leave
                    it blank to make the slide non-clickable.
                </p>
                <input
                    :id="`carousel-editor-url-${slide.id}`"
                    v-model="urlDraft"
                    type="text"
                    class="carousel-editor__input"
                    placeholder="https://example.com/sale"
                    :aria-describedby="`carousel-editor-url-hint-${slide.id}`"
                    data-testid="ct-carousel-editor-url"
                    @input="onFieldInput"
                    @blur="onFieldBlur"
                />
                <p
                    v-if="urlError"
                    class="carousel-editor__error"
                    data-testid="ct-carousel-editor-url-error"
                >
                    {{ urlError }}
                </p>
            </div>
        </div>

        <div class="carousel-editor__actions">
            <button
                v-if="!confirmingDelete"
                type="button"
                class="carousel-editor__btn carousel-editor__btn--danger"
                data-testid="ct-carousel-editor-delete"
                @click="onDeleteClick"
            >
                Delete
            </button>
            <button
                v-else
                type="button"
                class="carousel-editor__btn carousel-editor__btn--danger-confirm"
                data-testid="ct-carousel-editor-delete-confirm"
                @click="onConfirmDelete"
            >
                Confirm delete
            </button>
        </div>
    </div>
</template>

<script>
// Debounce window for auto-save after the last keystroke. Long enough to
// coalesce a burst of typing into one PATCH, short enough to feel live.
const AUTOSAVE_DEBOUNCE_MS = 600;

export default {
    name: "HeroCarouselEditor",

    props: {
        slide: {
            type: Object,
            required: true,
        },
    },

    emits: ["save", "delete"],

    data() {
        return {
            headlineDraft: this.slide.headline || "",
            urlDraft: this.slide.click_through_url || "",
            urlError: "",
            confirmingDelete: false,
            // The slide id the CURRENT drafts belong to. The save payload
            // carries it so the parent PATCHes the right row even when the
            // flush fires after selection has moved to another slide.
            draftSlideId: this.slide.id,
            // True once the user has actually edited a field. Guards against
            // a phantom save on open (drafts seeded from the incoming slide)
            // and against flushing an untouched slide on switch/unmount.
            dirty: false,
            // Pending debounce timer handle, or null when nothing is queued.
            saveTimer: null,
        };
    },

    watch: {
        // When the parent swaps in a different slide (user clicked another
        // thumbnail), FLUSH any pending edit for the outgoing slide FIRST --
        // otherwise a fast edit-then-switch drops the change -- then re-seed
        // the drafts for the incoming slide.
        slide: {
            handler(newSlide) {
                this.flushPendingSave();
                this.headlineDraft = newSlide.headline || "";
                this.urlDraft = newSlide.click_through_url || "";
                this.urlError = "";
                this.confirmingDelete = false;
                this.draftSlideId = newSlide.id;
                this.dirty = false;
            },
            deep: true,
        },
    },

    beforeUnmount() {
        // Flush a pending edit before the component goes away (e.g. the last
        // slide was deleted, or the panel unmounted) so nothing is dropped.
        this.flushPendingSave();
    },

    methods: {
        // URL validator. Empty string is valid (URL is optional). Non-empty
        // must parse via the URL constructor with an explicit protocol --
        // bare strings like "example.com" or "not a url" reject.
        isValidUrl(value) {
            if (!value) return true;
            try {
                const u = new URL(value);
                return u.protocol === "http:" || u.protocol === "https:";
            } catch (_e) {
                return false;
            }
        },

        // Called on every keystroke in the headline or URL field. Marks the
        // editor dirty and (re)arms the debounce timer.
        onFieldInput() {
            this.dirty = true;
            if (this.saveTimer) {
                clearTimeout(this.saveTimer);
            }
            this.saveTimer = setTimeout(() => {
                this.saveTimer = null;
                this.commitSave();
            }, AUTOSAVE_DEBOUNCE_MS);
        },

        // Called on blur of either field. Flush any pending debounced edit
        // immediately for responsiveness.
        onFieldBlur() {
            this.flushPendingSave();
        },

        // Cancel a queued debounce and, if there was one, persist now.
        flushPendingSave() {
            if (!this.saveTimer) return;
            clearTimeout(this.saveTimer);
            this.saveTimer = null;
            this.commitSave();
        },

        // Validate and emit the save. An invalid URL is NOT persisted -- the
        // error is shown and we keep the drafts so the user can correct them;
        // once corrected, the next edit re-arms auto-save. Never emits when
        // the editor is not dirty (no phantom save on open).
        commitSave() {
            if (!this.dirty) return;
            this.urlError = "";
            if (!this.isValidUrl(this.urlDraft)) {
                this.urlError =
                    "Enter a valid URL starting with http:// or https://";
                return;
            }
            this.$emit("save", {
                id: this.draftSlideId,
                headline: this.headlineDraft,
                click_through_url: this.urlDraft,
            });
            this.dirty = false;
        },

        onDeleteClick() {
            // Two-step confirm. First click reveals the confirm button.
            this.confirmingDelete = true;
        },

        onConfirmDelete() {
            this.$emit("delete");
            this.confirmingDelete = false;
        },
    },
};
</script>

<style scoped>
.carousel-editor {
    display: flex;
    flex-direction: column;
    gap: var(--tf-sp-3, 12px);
    margin-top: var(--tf-sp-3, 12px);
    padding: var(--tf-sp-3, 12px);
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-1, 4px);
    background: var(--tf-bg-2, var(--tf-bg));
}

/* Headline | click-through URL, two across, to save vertical space.
   align-items: start keeps the two columns top-aligned when one has a
   validation error and the other does not. */
.carousel-editor__fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--tf-sp-3, 12px);
    align-items: start;
}

/* Collapse to stacked on narrow viewports -- same 720px boundary the
   Brand identity card uses (ADR 0024), so the editor stays usable on the
   smaller screens this surface is edited on. */
@media (max-width: 720px) {
    .carousel-editor__fields {
        grid-template-columns: 1fr;
    }
}

.carousel-editor__row {
    display: flex;
    flex-direction: column;
    gap: var(--tf-sp-1, 4px);
    /* Grid children default to min-width:auto, which lets a long
       placeholder blow out the column instead of shrinking. */
    min-width: 0;
}

.carousel-editor__label {
    font-size: 13px;
    color: var(--tf-fg-4);
}

.carousel-editor__hint {
    font-size: 12px;
    line-height: 1.4;
    color: var(--tf-text-muted, var(--tf-fg-4));
    margin: 0;
}

.carousel-editor__input {
    padding: 6px 10px;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-1, 4px);
    font-size: 14px;
}

.carousel-editor__error {
    color: var(--tf-error, #c0392b);
    font-size: 12px;
    margin: 0;
}

.carousel-editor__actions {
    display: flex;
    justify-content: space-between;
    gap: var(--tf-sp-2, 8px);
    margin-top: var(--tf-sp-1, 4px);
}

.carousel-editor__btn {
    padding: 6px 14px;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-1, 4px);
    background: var(--tf-bg);
    cursor: pointer;
    font-size: 14px;
}

.carousel-editor__btn--danger {
    color: var(--tf-error, #c0392b);
}

.carousel-editor__btn--danger-confirm {
    background: var(--tf-error, #c0392b);
    color: var(--tf-fg-on-primary);
    border-color: var(--tf-error, #c0392b);
}
</style>
